import {NullableVatRateSchema, ServiceType} from "@mytour/shared";
import {FloatingUnitField} from "@mytour/ui/form";
import {FloatingField} from "@mytour/ui/form";
import type {UseFormReturn} from "react-hook-form";
import {Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {FormGroup, Input, Label} from "reactstrap";
import z from "zod";

export const step1Schema = z.object({
  label: z.string().min(3, {message: "Obligatoire"}),
  amount: z.number().min(0, {message: "Obligatoire"}),
  type: z.enum(["sale", "service"]),
  vatRate: NullableVatRateSchema,
});
export type Step1Form = z.infer<typeof step1Schema>;

type Props = {
  hookForm: UseFormReturn<Step1Form>;
  disabled: boolean;
  hasVatEnabled: boolean;
};

export function Step1Service({hookForm, disabled, hasVatEnabled}: Props) {
  const {t: translate} = useTranslation();
  const {
    control,
    formState: {errors},
  } = hookForm;

  return (
    <>
      <p style={{marginBottom: "24px"}} />

      <FloatingField<Step1Form>
        name="label"
        label={translate("myTourApp.serviceDescription.label")}
        control={control}
        error={errors.label}
        disabled={disabled}
        required
      />
      <FloatingUnitField<Step1Form>
        name="amount"
        label={translate("myTourApp.serviceDescription.amount")}
        suffix="€"
        control={control}
        disabled={disabled}
        step="0.01"
        min={0}
        required
      />

      {hasVatEnabled && (
        <FormGroup floating>
          <Controller
            name="vatRate"
            control={control}
            render={({field}) => (
              <Input
                type="select"
                id="onboarding-field-vatRate"
                disabled={disabled}
                placeholder="Taux de TVA"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
                onBlur={field.onBlur}
                name={field.name}
                innerRef={field.ref}
              >
                <option value="20">20% (taux normal)</option>
                <option value="10">10% (taux intermédiaire)</option>
                <option value="5.5">5,5% (taux réduit)</option>
                <option value="2.1">2,1% (taux particulier)</option>
              </Input>
            )}
          />
          <Label for="onboarding-field-vatRate">Taux de TVA</Label>
        </FormGroup>
      )}

      <FormGroup floating>
        <Controller
          name="type"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="select"
              id="field-type"
              placeholder={translate("myTourApp.serviceDescription.type")}
            >
              {Object.values(ServiceType).map((type) => (
                <option value={type} key={type}>
                  {translate(`myTourApp.serviceDescription.type_${type}`)}
                </option>
              ))}
            </Input>
          )}
        />
        <Label for="field-type">{translate("myTourApp.serviceDescription.type")}</Label>
      </FormGroup>
    </>
  );
}
